<template>
  <ButtonTemplateMicInput
    :text-button="micButtonText"
    :is-data-off="!systemMicrophoneStore.isMicOn && !isMicTurningOn"
    @click="toggleMic"
  >
    <template #icon-handler>
      <div class="button-mic-icon-wrapper">
        <component
          :is="systemMicrophoneStore.isMicOn ? MicIcon : MicOffIcon"
          :class="{ 'mic-turning-on': isMicTurningOn }"
        />
        <div class="recording">
          <div
            class="record-circle-dot"
            :class="{
              'dot-turning-on': isMicTurningOn,
              'dot-mic-on': systemMicrophoneStore.isMicOn,
            }"
          />
          <div v-if="systemMicrophoneStore.isMicOn" class="record-circle" />
        </div>
      </div>
    </template>
  </ButtonTemplateMicInput>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import MicIcon from '@/components/icons/IconMic.vue';
import MicOffIcon from '@/components/icons/IconMicOff.vue';
import useMCSSubUser from '@/composables/domains/chat/mcs/useMCSSubUser';
import useMultiSocketController from '@/composables/domains/chat/multiSocket/useMultiSocketController';
import useMicSignal from '@/composables/shared/microphone/useMicSignal';
import useSystemMicControl from '@/composables/shared/microphone/useSystemMicControl';
import { useCallStore } from '@/stores/call';
import { useSocketStore } from '@/stores/socket';
import { useSystemAudioMicrophoneStore } from '@/stores/systemAudioMicrophone';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { stringToBase64UrlSafe } from '@/utils/encryptor';

const props = withDefaults(
  defineProps<{
    signalMode?: boolean;
  }>(),
  {
    signalMode: false,
  }
);

const route = useRoute();
const { t } = useI18n();

const toastStore = useToastStore();
const userStore = useUserStore();
const socketStore = useSocketStore();
const callStore = useCallStore();
const systemMicrophoneStore = useSystemAudioMicrophoneStore();

const code = ref<string>('');

const { startMic, stopMic } = useSystemMicControl();
const { startMicrophone, stopMicrophone } = useMicSignal('SYSTEM_AUDIO_MIC');
const { isSocketConnected, setupSocket, connectSocket, disconnectSocket } = useMultiSocketController(false);
const { isValidUser, subUser, createUser } = useMCSSubUser(code, 'SYSTEM_AUDIO_MIC');

const isMicTurningOn = ref<boolean>(false);

const micEvent = computed(() => {
  return props.signalMode ? { start: startMicrophone, stop: stopMicrophone } : { start: startMic, stop: stopMic };
});

const micButtonText = computed<string>(() => {
  if (isMicTurningOn.value) {
    return t('turning_mic_on');
  }
  return systemMicrophoneStore.isMicOn ? t('audio_system_mic_on') : t('audio_system_mic_off');
});

const toggleMic = async () => {
  if (isMicTurningOn.value) {
    return;
  }
  try {
    isMicTurningOn.value = true;

    if (systemMicrophoneStore.isMicOn) {
      await micEvent.value.stop();
    } else {
      if (!isValidUser.value || !isSocketConnected.value) {
        await openSocket();
      }
      await micEvent.value.start();
    }
  } catch {
    toastStore.error(t('PROVIDER_ERROR'));
  } finally {
    isMicTurningOn.value = false;
  }
};

const initSocket = () => {
  systemMicrophoneStore.initSocketId();
  setupSocket({
    socketId: systemMicrophoneStore.socketId,
    connectionInfo: {
      code: code.value,
      userId: subUser.value.userId,
      chatId: socketStore.selectedChatId,
      nickname: stringToBase64UrlSafe(subUser.value.nickname),
      languageCode: userStore.pageLanguage,
      token: `Bearer ${subUser.value.authToken}`,
      parentToken: `Bearer ${localStorage.getItem('authToken')}`,
      callType: callStore.qrCodeInfo.callType,
    },
  });
};

const openSocket = async () => {
  try {
    await createUser();
    initSocket();
    await connectSocket();
  } catch {
    throw new Error();
  }
};

const closeSocket = async () => {
  disconnectSocket();
  if (systemMicrophoneStore.isMicOn) {
    await toggleMic();
  }
};

watch(
  () => isSocketConnected.value,
  async (val) => {
    if (!val && systemMicrophoneStore.isMicOn) {
      await toggleMic();
    }
  }
);

watch(
  () => route.fullPath,
  () => {
    code.value = String(route.params.code ?? route.query.code ?? '');
  },
  { immediate: true }
);

onMounted(async () => {
  systemMicrophoneStore.initMicErrorEvent(micEvent.value.stop);
});

onUnmounted(async () => {
  await closeSocket();
});
</script>

<style scoped lang="scss">
.button-mic-icon-wrapper {
  position: relative;
  display: flex;
  height: 1.75rem;
  align-items: start;
  justify-content: space-between;
}

.recording {
  position: relative;
  display: block;
  width: 2rem;
  height: 1.75rem;
  left: 0.75rem;
}

.record-circle {
  position: absolute;
  background: var(--background-dot);
  border-radius: 50%;
  animation: pulse 1s infinite;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.7;
}

.record-circle-dot {
  width: 0.5rem;
  height: 0.5rem;
  background: var(--dot);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 999;
}

.record-circle-dot.dot-turning-on {
  opacity: 0.3;
}

.record-circle-dot.dot-mic-on {
  background: var(--dot-active);
}

::v-deep(.button-mic) {
  align-items: center;
  justify-content: flex-start;
  min-width: 5rem;
}
</style>
